import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import { CustomValidators } from 'ng2-validation';
import { takeUntil } from 'rxjs/operators';
import {
  EventsService,
  EventType,
  TriggerNotificationEventParams,
  Unsubscribe,
} from 'src/app/core';
import { NotificationService } from 'src/app/core/services/notification.service';

@Component({
  selector: 'app-end-users-messaging',
  templateUrl: './end-users-messaging.component.html',
  styleUrls: ['./end-users-messaging.component.less'],
})
export class EndUsersMessagingComponent extends Unsubscribe implements OnInit {
  isSubmitting = false;
  messagingEvent: EventType = EventType.SMS;
  messagingEventLabel = 'SMS';
  messagingForm: FormGroup;
  eventType = EventType;
  MESSAGE_MAX_LENGTH = 400;
  notificationEvent: TriggerNotificationEventParams =
    {} as TriggerNotificationEventParams;
  notificationExpirations: any[] = [
    {
      digit: '1',
      text: 'HOUR',
      value: this.convertExpirationDate(1 * 60 * 60 * 1000),
    },
    {
      digit: '2',
      text: 'HOURS',
      value: this.convertExpirationDate(2 * 60 * 60 * 1000),
    },
    {
      digit: '6',
      text: 'HOURS',
      value: this.convertExpirationDate(6 * 60 * 60 * 1000),
    },
    {
      digit: '12',
      text: 'HOURS',
      value: this.convertExpirationDate(12 * 60 * 60 * 1000),
    },
    {
      digit: '1',
      text: 'DAY',
      value: this.convertExpirationDate(24 * 60 * 60 * 1000),
    },
    {
      digit: '1',
      text: 'WEEK',
      value: this.convertExpirationDate(7 * 24 * 60 * 60 * 1000),
    },
  ];
  SEND_MSG_PREFIX = 'PAGE.MOBILE.END_USERS.SEND_MESSAGE';

  @Input() lineId: string;
  @Input() brandUuid: string;

  constructor(
    private fb: FormBuilder,
    private eventsService: EventsService,
    private drawerRef: NzDrawerRef<string>,
    private notificationService: NotificationService
  ) {
    super();
  }

  ngOnInit(): void {
    this.messagingForm = this.fb.group(this.messagingFormModel());
    this.generateEventValuesFormModel();
  }

  getStrPath(path) {
    return `${this.SEND_MSG_PREFIX}.${path}`;
  }

  messagingFormModel() {
    return {
      event: [this.messagingEvent, [Validators.required]],
      lineId: [this.lineId, [Validators.required]],
      brandUuid: [this.brandUuid, [Validators.required]],
      eventValues: this._smsFormGroup(),
    };
  }

  private _smsFormGroup(): FormGroup {
    return this.fb.group({
      message: [
        '',
        [Validators.required, Validators.maxLength(this.MESSAGE_MAX_LENGTH)],
      ],
    });
  }

  private _subBrandingFormGroup(): FormGroup {
    return this.fb.group({
      body: ['', [Validators.required]],
      title: ['', [Validators.required]],
      expiration: ['', [Validators.required]],
      network_name: ['', [Validators.required]],
      sub_brand_folder: ['', [Validators.required]],
    });
  }

  private _appPushFormGroup(): FormGroup {
    return this.fb.group({
      body: ['', [Validators.required]],
      title: ['', [Validators.required]],
    });
  }

  private _appPushUrlFormGroup(): FormGroup {
    return this.fb.group({
      title: ['', [Validators.required]],
      body: ['', [Validators.required]],
      redirect_url: ['', [Validators.required, CustomValidators.url]],
    });
  }

  convertExpirationDate(expiration: number) {
    return new Date(new Date().getTime() + expiration).toISOString();
  }

  generateEventValuesFormModel() {
    let group: FormGroup;

    switch (this.messagingEvent) {
      case EventType.SMS:
        group = this._smsFormGroup();
        break;
      case EventType.SUB_BRANDING:
        group = this._subBrandingFormGroup();
        break;
      case EventType.APP_PUSH:
        group = this._appPushFormGroup();
        break;
      case EventType.APP_PUSH_URL:
        group = this._appPushUrlFormGroup();
        break;

      default:
        group = this._smsFormGroup();
        break;
    }

    this.messagingForm.get('event').setValue(this.messagingEvent);
    this.messagingForm.setControl('eventValues', group);
  }

  /**
   * Update the messaging event
   * @param messagingEvent
   */
  setMessagingEvent(messagingEvent: EventType): void {
    this.updateMessagingEventLabel(messagingEvent);
  }

  /**
   * Update the messaging event label
   * @param messagingEvent
   */
  updateMessagingEventLabel(messagingEvent: EventType) {
    switch (messagingEvent) {
      case EventType.SMS:
        this.messagingEventLabel = this.getStrPath('SMS.TITLE');
        break;
      case EventType.SUB_BRANDING:
        this.messagingEventLabel = this.getStrPath('SUB_BRANDING.TITLE');
        break;
      case EventType.APP_PUSH:
        this.messagingEventLabel = this.getStrPath('APP_PUSH.TITLE');
        break;
      case EventType.APP_PUSH_URL:
        this.messagingEventLabel = this.getStrPath('APP_PUSH_URL.TITLE');
        break;
      default:
        this.messagingEventLabel = this.getStrPath('SMS.TITLE');
        break;
    }
    this.messagingEvent = messagingEvent;

    this.generateEventValuesFormModel();
  }

  /**
   * Submit notification event
   */
  submitForm(): void {
    this.isSubmitting = true;
    if (this.messagingForm.valid) {
      this.eventsService
        .triggerNotificationEvent(this.messagingForm.value)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(
          () => {
            this.isSubmitting = false;
            this.closeDrawer();
            this.notificationService.success(
              'MESSAGE.SUCCESS',
              this.getStrPath('SUCCESS_MSG')
            );
          },
          () => (this.isSubmitting = false)
        );
    }
  }

  closeDrawer(): void {
    this.drawerRef.close();
  }
}
